import { Injectable } from "@angular/core";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { BehaviorSubject, forkJoin, Observable } from "rxjs";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { TranslationService } from "@core-services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { config } from "@config";
import { PofStateService } from "@modules/pof/domain/+state/pof.state.service";
import { ToastrService } from "ngx-toastr";
import { SegmentService } from "@main-services/segment.service";
import { PofTypeId } from "@modules/pof/components/pof-pow-type.enum";
import { OfflineError } from "@models";
import { HttpHeaders } from "@angular/common/http";
import { OfflineModalComponent } from "@modules/main/components/offline-modal/offline-modal.component";
import { ModalService } from "@core-services/modal.service";
import { findContentTypeDocs, stripSpecialCharacters } from "@modules/pof/util/util";
import { CONTENT_TYPE_OCTET_STREAM } from "@helpers/constants";
import { PromptsFacade } from "@modules/messaging-prompts/domain/+state/prompts.facade";
import { RudderstackService } from "@main-services/rudderstack.service";

@UntilDestroy()
@Injectable()
export class PofFacade {
	constructor(
		private readonly apicallService: ApicallService,
		private readonly translationService: TranslationService,
		private readonly service: UniversalModalService,
		private readonly segmentService: SegmentService,
		private readonly rudderstackService: RudderstackService,
		private readonly toastr: ToastrService,
		private readonly pofStateService: PofStateService,
		private readonly modalService: ModalService,
		private readonly promptsFacade: PromptsFacade
	) {
		// The following 3 lines of code have been implemented to clean the filesList array after the user close the modal
		service.close$.pipe(untilDestroyed(this)).subscribe((modal) => {
			if (modal !== null && modal.reason === ModalCloseReason.close) {
				this.cleanFilesList();
			}
		});
	}

	private readonly successTitle: string = "pof_provide_source_of_funds_success_message_title";
	private readonly successBody: string = "pof_provide_source_of_funds_success_message_body";
	private readonly errorMessageTitle: string = "pof_provide_source_of_funds_upload_failed_message_title";
	private readonly errorMessageBody: string = "pof_provide_source_of_funds_upload_failed_message_body";
	private isUploadInProgress: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public isUploadInProgress$(): Observable<boolean> {
		return this.isUploadInProgress.asObservable();
	}

	private showUploadSuccessMessage(): void {
		this.toastr.success(
			this.translationService.translate(this.successBody),
			this.translationService.translate(this.successTitle)
		);
		this.isUploadInProgress.next(false);
	}

	private cleanFilesList(): void {
		this.setFilesList([]);
	}

	public setFilesList(files: File[]): void {
		this.pofStateService.updateState(files);
	}

	public getFilesList$(): Observable<File[]> {
		return this.pofStateService.getState$();
	}

	public sendFiles(files: File[], caseID: string, typeId: string, pid: string): void {
		const apiCalls = [];

		const basicHeaders = {
			"Case-Documents-Total-Count": files.length.toString(),
			"Session-Id": Date.now().valueOf().toString(),
			"Process-Type": typeId.toUpperCase(),
			"Content-Type": CONTENT_TYPE_OCTET_STREAM
		};

		files.forEach((file, index) => {
			const option = new ApicallOption(null, file);
			option.headers = new HttpHeaders({
				"Appian-Document-Name": stripSpecialCharacters(file.name),
				"Document-Number": (++index).toString(), // Document number starts from 1
				"Content-Type": findContentTypeDocs(file.name, file.type),
				...basicHeaders
			});

			apiCalls.push(this.apicallService.postFile(`proof-of-funds/upload/${caseID}`, option));
		});

		this.isUploadInProgress.next(true);

		setTimeout(() => {
			forkJoin(apiCalls).subscribe(
				() => {
					this.promptsFacade.updatePromptsData(pid, caseID);
					this.showUploadSuccessMessage();
					this.handleSuccessToastDisplayed(typeId);
					this.service.close(ModalCloseReason.close);
				},
				(error) => {
					this.isUploadInProgress.next(false);

					if (error instanceof OfflineError) {
						this.modalService.create(OfflineModalComponent).present();
						return;
					}

					this.toastr.error(
						this.translationService.translate(this.errorMessageBody),
						this.translationService.translate(this.errorMessageTitle)
					);
					return;
				}
			);
		}, 200);
	}

	private handleScreenDisplayed(typeId: string): string {
		return typeId === PofTypeId.pof ? "PoF_file_upload_screen" : "PoW_file_upload_screen";
	}

	public handleAddFilesClicked(typeId: string): void {
		this.rudderstackTracker("Add Files Button Clicked", this.handleScreenDisplayed(typeId));
	}

	private handleSuccessToastDisplayed(typeId: string): void {
		this.rudderstackTracker("File Upload Success Toast Displayed", this.handleScreenDisplayed(typeId));
	}

	private rudderstackTracker(name: string, screen_on: string): void {
		this.segmentService.track({
			name: name,
			properties: {
				screen_it_happened_on: screen_on
			}
		});
		this.rudderstackService.track({
			name: name,
			properties: {
				screen_it_happened_on: screen_on
			}
		});
	}

	public getHelpdeskLink(): void {
		if (!this.isUploadInProgress.getValue()) {
			window.open(this.translationService.getHdLink() + config.externalPaths["pof"]["link"]);
		}
	}
}
